import React, { useCallback, useState } from 'react';

/**
 * A photo tile with an optional face-box outline and a circular face avatar.
 *
 * Face boxes are [x, y, w, h] normalized to the full image (0–1). The tiles
 * render the photo with object-fit: cover inside a square, so mapping those
 * coords straight to container percentages only works for square images —
 * every portrait or landscape photo shifted the box and stretched the avatar
 * face. Both overlays need the image's real aspect ratio, read from
 * naturalWidth/naturalHeight once the tile image loads.
 */

const validBox = (box) =>
  Array.isArray(box) && box.length === 4 && box[2] > 0 && box[3] > 0;

// Where the face box lands inside a SQUARE container whose image is drawn
// with object-fit: cover. `ratio` is naturalWidth / naturalHeight.
export const coverBoxStyle = (box, ratio) => {
  const [x, y, w, h] = box;
  // Displayed image size in container units: cover scales the image so the
  // smaller side fills, letting the larger side overflow symmetrically.
  const dispW = ratio > 1 ? ratio : 1;
  const dispH = ratio > 1 ? 1 : 1 / ratio;
  return {
    left: `${(x * dispW - (dispW - 1) / 2) * 100}%`,
    top: `${(y * dispH - (dispH - 1) / 2) * 100}%`,
    width: `${w * dispW * 100}%`,
    height: `${h * dispH * 100}%`,
  };
};

// Zoomed crop for the circular avatar: one uniform scale (no stretching),
// chosen so the face's larger side fills the circle, centered on the face.
export const avatarImgStyle = (box, ratio) => {
  const [x, y, w, h] = box;
  let widthPct;
  let heightPct;
  // Face pixel width vs height: (w * naturalW) vs (h * naturalH) ⇔ w*ratio vs h.
  if (w * ratio >= h) {
    widthPct = 100 / w;
    heightPct = widthPct / ratio;
  } else {
    heightPct = 100 / h;
    widthPct = heightPct * ratio;
  }
  return {
    position: 'absolute',
    width: `${widthPct}%`,
    height: `${heightPct}%`,
    left: `${50 - (x + w / 2) * widthPct}%`,
    top: `${50 - (y + h / 2) * heightPct}%`,
    maxWidth: 'none',
  };
};

// Measures an <img>'s natural aspect ratio. Cached images can already be
// complete before onLoad is attached, so the ref measures too.
const useNaturalRatio = () => {
  const [ratio, setRatio] = useState(null);

  const measureRef = useCallback((img) => {
    if (img && img.complete && img.naturalWidth > 0) {
      setRatio(img.naturalWidth / img.naturalHeight);
    }
  }, []);

  const onLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth > 0) setRatio(img.naturalWidth / img.naturalHeight);
  };

  return { ratio, measureRef, onLoad };
};

/**
 * A standalone circular avatar cropped to the person's face.
 *
 * The consumer's className owns size, border and border-radius; the wrapper
 * supplies position/overflow inline so the crop can never leak out of it.
 * Falls back to a plain cover-fit image until the ratio is known, or when the
 * photo has no stored face box (anything uploaded before boxes existed).
 */
export const FaceAvatar = ({ src, box, className = '', alt = '' }) => {
  const { ratio, measureRef, onLoad } = useNaturalRatio();
  const cropped = validBox(box) && ratio;
  return (
    <div className={className} style={{ position: 'relative', overflow: 'hidden' }}>
      <img
        src={src}
        alt={alt}
        draggable={false}
        ref={measureRef}
        onLoad={onLoad}
        style={
          cropped
            ? avatarImgStyle(box, ratio)
            : { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
        }
      />
    </div>
  );
};

const FacePhoto = ({
  src,
  box,
  showBox = false,
  boxClassName = 'pt-face-box',
  avatarClassName = 'pt-face-box-avatar',
  imgProps = {},
}) => {
  const { ratio, measureRef, onLoad: handleLoad } = useNaturalRatio();

  const hasBox = validBox(box);

  return (
    <>
      <img
        src={src}
        alt=""
        draggable={false}
        ref={measureRef}
        onLoad={handleLoad}
        {...imgProps}
      />
      {hasBox && ratio && showBox && (
        <div className={boxClassName} style={coverBoxStyle(box, ratio)} />
      )}
      {hasBox && ratio && (
        <div className={avatarClassName}>
          <img src={src} alt="" draggable={false} style={avatarImgStyle(box, ratio)} />
        </div>
      )}
    </>
  );
};

export default FacePhoto;
